import { Contact } from 'components/Contact';
import { CourseCTA } from 'components/CourseCTA';
import { getDocByUID, getDocs } from 'shared/prismic';
import { getReviews } from 'shared-server/firebase';
import { GREEN, GREY, GREY_BORDER } from 'shared/variables';
import { Hero } from 'components/Hero';
import { Meta } from 'components/Meta';
import { Page } from 'components/Page';
import { RelatedItemsWrapper } from 'components/RelatedItems';
import { Slices } from 'components/Slices';
import { SpacingSection } from 'components/SpacingSection';

export async function getStaticProps({
  preview = null,
  previewData,
  params: { uid },
}) {
  const reviews = await getReviews(uid);

  const reviewScores = (reviews || []).map(({ score }) => parseInt(score, 10));
  const reviewsAmount = reviewScores.length;
  const reviewsRating =
    reviewsAmount > 0
      ? reviewScores.reduce((a, b) => a + b, 0) / reviewsAmount
      : 0;

  const doc = await getDocByUID({
    previewData,
    uid: uid,
    type: 'course_detail',
  });

  return {
    props: { doc, reviewsAmount, reviewsRating, preview },
  };
}

export async function getStaticPaths() {
  const pages = await getDocs({ type: 'course_detail' });
  const paths = pages.map(({ uid }) => ({ params: { uid } }));
  return { paths, fallback: false };
}

const Index = ({ doc, reviewsAmount, reviewsRating, preview }) => {
  const {
    seo_title,
    seo_description,
    seo_image,
    hero_image,
    hero_title,
    enroll_link,
    show_studyguide,
    show_reviews,
    body,
    meta_duration,
    meta_level,
    meta_investment,
    meta_start,
    meta_cost,
    meta_location,
    cta_title,
    cta_event_label,
    cta_event_link,
    contact_title,
    contact_email,
    contact_tel,
    contact_whatsapp,
    related_items,
    related_items_title,
  } = doc.data;

  const renderContact =
    contact_title || contact_email || contact_tel || contact_whatsapp;
  const renderHero = hero_image?.alt || hero_image?.src || hero_title;
  const renderMeta =
    meta_duration ||
    meta_level ||
    meta_investment ||
    meta_start ||
    meta_cost ||
    meta_location;
  const renderRelated = related_items?.length > 0 || related_items_title;
  const renderCTA = !!cta_title;
  const renderSlices = body?.length > 0;

  const studyGuideButtonData = {
    color: GREEN,
    label: 'Studiegids aanvragen',
    link: { type: 'course_studyguide', uid: doc.uid },
  };

  const metaButtons = show_studyguide ? [studyGuideButtonData] : [];
  const ctaButtons = show_studyguide ? [studyGuideButtonData] : [];

  if (enroll_link?.uid) {
    metaButtons.push({
      color: GREY_BORDER,
      label: 'Direct aanmelden',
      link: enroll_link,
    });
    ctaButtons.push({
      color: GREY,
      label: 'Direct aanmelden',
      link: enroll_link,
    });
  }

  if (cta_event_label && cta_event_link) {
    ctaButtons.push({
      color: GREY,
      label: cta_event_label,
      link: cta_event_link,
    });
  }

  const reviewsProps = show_reviews
    ? {
        reviewsAmount,
        reviewsRating,
        reviewsLink: { type: 'course_reviews', uid: doc.uid },
      }
    : {};

  return (
    <Page
      title={seo_title}
      description={seo_description}
      image={seo_image}
      preview={preview}
    >
      {renderHero && (
        <Hero
          image={{ alt: hero_image.alt, src: hero_image.url }}
          title={hero_title}
        />
      )}
      {renderMeta && (
        <Meta
          meta={[
            { icon: 'duration', label: 'Duur', value: meta_duration },
            { icon: 'level', label: 'Niveau', value: meta_level },
            {
              icon: 'investment',
              label: 'Studie-investering',
              value: meta_investment,
            },
            { icon: 'start', label: 'Eerstvolgende start', value: meta_start },
            { icon: 'costs', label: 'Kosten', value: meta_cost },
            { icon: 'location', label: 'Locatie', value: meta_location },
          ]}
          buttons={metaButtons}
          {...reviewsProps}
        />
      )}
      {renderSlices && <Slices data={body} uid={doc.uid} />}
      {renderCTA && <CourseCTA title={cta_title} buttons={ctaButtons} />}
      {renderContact && (
        <Contact
          title={contact_title}
          email={contact_email}
          tel={contact_tel}
          whatsapp={contact_whatsapp}
        />
      )}
      {renderRelated && (
        <SpacingSection>
          <RelatedItemsWrapper
            related_items={related_items}
            related_items_title={related_items_title}
          />
        </SpacingSection>
      )}
    </Page>
  );
};

export default Index;
